/**
 * Snapper for the nested 2, 4 and 8 km location grid (docs#10).
 *
 * Port of geo `grid/location_grid.py`. The steps and their order follow the geo README
 * "Port contract", so a position on a cell boundary lands in the same cell as in PHP and Python.
 * `scripts/tests/location-grid.test.js` checks this file against the shared fixture.
 */

const TIER_SHIFTS = { 2: 0, 4: 1, 8: 2 };

function tierShift(tierKm) {
    const shift = TIER_SHIFTS[tierKm];
    if (shift === undefined) {
        throw new RangeError('unknown tier: ' + tierKm);
    }
    return shift;
}

/**
 * Build a snapper from the parsed grid-spec.json.
 * @param {{version: number, n: number, n8: number[], cap_min_n8: number, col_bits: number}} spec
 */
export function createLocationGrid(spec) {
    const n = spec.n;
    const n8 = spec.n8;
    const capMinN8 = spec.cap_min_n8;
    const colBits = spec.col_bits;
    if (n8.length !== n) {
        throw new RangeError('n8 table length does not match n');
    }

    const rows = (tierKm) => (n * 4) >> tierShift(tierKm);
    const cols = (tierKm, row) => {
        const shift = tierShift(tierKm);
        return (n8[row >> (2 - shift)] * 4) >> shift;
    };
    const isCap = (tierKm, row) => n8[row >> (2 - tierShift(tierKm))] < capMinN8;

    function centre(tierKm, row, col) {
        if (isCap(tierKm, row)) {
            return row < rows(tierKm) / 2 ? [-90, 0] : [90, 0];
        }
        return [
            -90 + (row + 0.5) * 180 / rows(tierKm),
            -180 + (col + 0.5) * 360 / cols(tierKm, row),
        ];
    }

    function cell(lat, lon, tierKm) {
        if (typeof lat !== 'number' || typeof lon !== 'number'
            || !Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90) {
            throw new RangeError('position out of range');
        }
        const shift = tierShift(tierKm);
        const rows2 = 4 * n;
        const row2 = Math.min(Math.max(Math.floor((lat + 90) * rows2 / 180), 0), rows2 - 1);
        const row8 = row2 >> 2;
        let row;
        let col;
        if (n8[row8] < capMinN8) {
            row = row8 < Math.floor(n / 2) ? 0 : rows(tierKm) - 1;
            col = 0;
        } else {
            const cols2 = 4 * n8[row8];
            let lonNorm = lon + 180;
            lonNorm = lonNorm - 360 * Math.floor(lonNorm / 360);
            const col2 = Math.min(Math.max(Math.floor(lonNorm * cols2 / 360), 0), cols2 - 1);
            row = row2 >> shift;
            col = col2 >> shift;
        }
        return {
            tierKm,
            row,
            col,
            cap: isCap(tierKm, row),
            cellId: ((row << colBits) | col) >>> 0,
            centre: centre(tierKm, row, col),
        };
    }

    return {
        version: spec.version,
        cell,
        /** The centre of the 2 km cell that holds the position, as [lat, lon]. */
        snapToTwoKmCentre(lat, lon) {
            return cell(lat, lon, 2).centre;
        },
    };
}
